import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, extractApiError } from '../../lib/api';
import { deriveTemplateApprovalPolicy, type SiteApprovalLineMetaData } from '@safetyhub/shared';
import ApprovalLineMetaCard from './ApprovalLineMetaCard';
import SaveWithReasonModal from './SaveWithReasonModal';
import HistoryModal from './HistoryModal';
import { useDirtyChildren, useReportDirty } from '../../hooks/useChildDirty';

// 현장 결재선의 축 값(templateKey)은 Composer 템플릿 key다(lowercase-hyphen, 예: 'safety-management-status-report').
// 카드 목록은 공개(enabled) 템플릿에서만 파생한다. 결정: docs/decisions/2026-07-24-composed-approval-site-axis.md
interface TemplateKeyEntry {
  templateKey: string;
  label: string;
}

interface SiteApprovalLineTabProps {
  siteId: string;
  /** 미저장 닫기 보호 — 편집 중인 카드가 하나라도 dirty면 true를 올린다(현장 상세가 `isDirty`에 더한다). */
  onDirtyChange?: (dirty: boolean) => void;
}

export default function SiteApprovalLineTab({ siteId, onDirtyChange }: SiteApprovalLineTabProps) {
  const queryClient = useQueryClient();
  const dirtyCards = useDirtyChildren();
  useReportDirty(dirtyCards.anyDirty, onDirtyChange);
  const [pendingSave, setPendingSave] = useState<SiteApprovalLineMetaData | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'approval-line-meta', 'site', siteId],
    queryFn: () => adminApi.getSiteApprovalLineMetas(siteId),
    enabled: !!siteId,
  });

  // Composer 템플릿 목록 — 카드 목록의 정본. 실패하면 카드가 없으므로 아래 배너로 알린다.
  const { data: templatesData, isError: templatesFailed } = useQuery({
    queryKey: ['admin', 'report-composer', 'templates'],
    queryFn: () => adminApi.listReportTemplates(),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      templateKey,
      meta,
      reason,
    }: {
      templateKey: string;
      meta: SiteApprovalLineMetaData;
      reason: string;
    }) =>
      // 전송 축은 결재선(lines)뿐 — 한도·활성은 양식 값이라 보내지 않는다(서버 whitelist, R1 컬럼 DROP).
      adminApi.updateSiteApprovalLineMeta(siteId, templateKey, {
        lines: meta.defaultApprovalLine,
        reason,
      }),
    onSuccess: () => {
      setSuccess('현장 결재라인 정책이 저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['admin', 'approval-line-meta', 'site', siteId] });
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  const handleSaveRequest = (meta: SiteApprovalLineMetaData) => {
    // 빈 결재선 현장 override는 서버가 무시한다(resolveComposedApprovalMeta는 rows.length>0일 때만
    // 채택). 그대로 두면 저장은 성공하는데 실제 결재선은 템플릿/상위 기본값이라, 배지만 "현장
    // override"로 뜨는 "저장했는데 안 먹는다" 상태가 된다. 저장 자체를 막아 오인을 차단한다.
    if (meta.hasApprovalLinePolicy && meta.defaultApprovalLine.length === 0) {
      setError('현장 기본 결재선은 최소 1단계가 필요합니다. 상위 기본값을 쓰려면 "현장 기본으로 되돌리기"를 이용하세요.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    setPendingSave(meta);
  };

  const handleConfirmSave = (reason: string) => {
    if (!pendingSave) return;
    updateMutation.mutate({ templateKey: pendingSave.templateKey, meta: pendingSave, reason });
    setPendingSave(null);
  };

  const siteOverrides = data?.data?.siteOverrides ?? [];

  // 비공개(enabled=false) 템플릿은 제외 — 보고서를 만들 수 없는 양식의 현장 기본은 무의미하다.
  const templates = (templatesData?.data ?? []).filter((t) => t.enabled);

  const entries: TemplateKeyEntry[] = templates.map((t) => ({
    templateKey: t.key,
    label: t.name,
  }));

  // 마지막 층(system 층)은 템플릿 approvalLineMeta다(결정 3 "템플릿이 system 층").
  // 서버 cascade(ReportComposerService.resolveComposedApprovalMeta)는 현장 → 템플릿 두 층이다(조직 override 층은 없다).
  // ⚠️ 파생 규칙은 서버(`resolveSystemLayer`)와 같은 shared `deriveTemplateApprovalPolicy` 하나다 — 정책 유무는
  //    템플릿 defaultApprovalLine 단계 존재로 파생한다. 여기 손으로 적거나 무조건 true로 두면 고정 단계가
  //    없는 템플릿이 화면에서만 "정책 있음"으로 보여 서버 판정과 어긋난다.
  //    현장 override 행(`siteOverrides`)의 한도·활성도 서버가 같은 규칙으로 양식에서 채워 준다 — 현장 행에 그
  //    두 컬럼은 더 이상 없다(2026-09-16 R1 DROP). 그래서 현장 override와 이 base는 그 두 값이 항상 같다.
  const getTemplateBase = (templateKey: string): SiteApprovalLineMetaData | undefined => {
    const template = templates.find((t) => t.key === templateKey);
    if (!template) return undefined;
    const policy = deriveTemplateApprovalPolicy(template.approvalLineMeta);
    return {
      templateKey,
      defaultApprovalLine: policy.base,
      maxAdditionalLines: policy.maxAdditionalLines,
      hasApprovalLinePolicy: policy.hasApprovalLinePolicy,
      baseNonLockedCount: policy.baseNonLockedCount,
      version: 0,
    };
  };

  const getSiteOverride = (templateKey: string): SiteApprovalLineMetaData | undefined =>
    siteOverrides.find((m) => m.templateKey === templateKey);

  const getEffective = (entry: TemplateKeyEntry): SiteApprovalLineMetaData | undefined =>
    getSiteOverride(entry.templateKey) ?? getTemplateBase(entry.templateKey);

  const getOverrideLabel = (entry: TemplateKeyEntry) => {
    if (getSiteOverride(entry.templateKey)) return { text: '현장 override', color: 'purple' };
    return { text: '양식 default 따름', color: 'gray' };
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-40 text-gray-400">로딩 중...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          현장별 보고서 양식의 결재라인 정책을 관리합니다. (현장 override → 양식(Composer) default 순)
        </p>
        <button
          type="button"
          onClick={() => setShowHistory(true)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
        >
          변경 이력
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>닫기</button>
        </div>
      )}
      {templatesFailed && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded text-sm">
          Composer 양식 목록을 불러오지 못했습니다. 양식별 결재선을 표시할 수 없습니다.
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {entries.map((entry) => {
          const effective = getEffective(entry);
          const base = getTemplateBase(entry.templateKey);
          const overrideLabel = getOverrideLabel(entry);

          if (!effective) return null;

          // 키가 곧 색 이름인 팔레트 — 이름과 값을 갈라 두지 않는다(Badge.tsx와 같은 이유).
          const colorClasses: Record<string, string> = {
            purple: 'bg-purple-100 text-purple-700',
            gray: 'bg-gray-100 text-gray-500',
          };

          return (
            <div key={entry.templateKey}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-gray-600">{entry.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${colorClasses[overrideLabel.color]}`}>
                  {overrideLabel.text}
                </span>
              </div>
              <ApprovalLineMetaCard
                meta={effective}
                baseMeta={base}
                showDiff={!!getSiteOverride(entry.templateKey)}
                displayLabel={entry.label}
                maxAdditionalLinesSourceLabel="양식(Composer) 설정"
                onSaveRequest={handleSaveRequest}
                onDirtyChange={(dirty) => dirtyCards.report(entry.templateKey, dirty)}
              />
            </div>
          );
        })}
      </div>

      <SaveWithReasonModal
        isOpen={!!pendingSave}
        onClose={() => setPendingSave(null)}
        onConfirm={handleConfirmSave}
        isLoading={updateMutation.isPending}
      />

      <HistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
      />
    </div>
  );
}
